import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  getDisabledSaveSearchActionButtonTooltip,
  shouldSaveSearchActionButtonDisabled,
} from '~/components/SearchV2/SearchV2.utils';
import { showToast } from '~/components/Shared/EcToast/showToast';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { api } from '~/redux/api/createApi';
import * as selectors from '~/redux/selectors';
import search from '~/redux/slices/search';
import { MODAL_DUPLICATE_SAVED_SEARCH } from '~/types/modal.types';
import { ERROR, SUCCESS } from '~/types/toast.types';

import SaveSearchForm from '../SaveSearchForm/SaveSearchForm';

const DuplicateSavedSearchModal = () => {
  const intl = useIntl();
  const modal = useSelector(selectors.selectSearchResultModal);
  const dispatch = useDispatch();
  const resetModal = () => dispatch(search.actions.setModal());
  const { modalType, context } = modal || {};

  const [saveSearch, saveSearchResult] = api.endpoints.saveSearch.useMutation();
  const {
    isError: duplicateSavedSearchError,
    isSuccess: duplicateSavedSearchSuccess,
    isLoading: isSavingSearch,
  } = saveSearchResult;

  const [formData, setFormData] = useState();

  useEffect(() => {
    if (duplicateSavedSearchError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'DuplicateSavedSearchModal.duplicateFailedMessage',
          defaultMessage: 'Duplicate saved search failed. Please try again.',
          description: 'Error message when duplicating saved search fails',
        }),
      );
    }
  }, [duplicateSavedSearchError]);

  useEffect(() => {
    if (duplicateSavedSearchSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'DuplicateSavedSearchModal.duplicateSuccessMessage',
            defaultMessage: '{name} search has been successfully duplicated.',
            description:
              'Success message when saved search is duplicated. Example: name = "Document Name"',
          },
          { name: context?.name },
        ),
      );
    }
  }, [duplicateSavedSearchSuccess]);

  return (
    <Modal
      primaryAction={{
        disabled: shouldSaveSearchActionButtonDisabled(formData),
        text: intl.formatMessage({
          id: 'DuplicateSavedSearchModal.duplicateButton',
          defaultMessage: 'Duplicate',
          description: 'Duplicate button text',
        }),
        tooltip: getDisabledSaveSearchActionButtonTooltip(formData, intl),
        onClick: () => {
          saveSearch(formData).then(() => {
            resetModal();
          });
        },
        isLoading: isSavingSearch,
      }}
      overflow="visible"
      title={intl.formatMessage({
        id: 'DuplicateSavedSearchModal.modalTitle',
        defaultMessage: 'Duplicate Saved Search',
        description: 'Title for duplicate saved search modal',
      })}
      isVisible={modalType === MODAL_DUPLICATE_SAVED_SEARCH}
      onCancel={resetModal}
      onHide={resetModal}
    >
      <SaveSearchForm isDuplicate={true} handleFormDataChanges={setFormData} />
    </Modal>
  );
};

export default DuplicateSavedSearchModal;
